import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  MasterFieldTypeEnum,
  ProductMasterRecordValue,
} from '@ulysses-inc/harami_api_client'
import { FC } from 'react'
import { semanticColors } from 'src/features/theme/semanticColors'
import { useQueryMaster } from '../../api/useQueryMaster'
import { LimitAlertTableRow } from './components/LimitAlertTableRow/LimitAlertTableRow'

type DataType = {
  key: React.Key
  name: string
  code: string
  [key: string]: string | number | React.Key | undefined // 動的なフィールド用
}

type ColumnDef = {
  key: string
  title: string
  dataIndex: string
  width: number
}

const productCodeWidth = 160
const productNameWidth = 280

const baseColumns: ColumnDef[] = [
  {
    key: 'code',
    title: '製品管理番号',
    dataIndex: 'code',
    width: productCodeWidth,
  },
  {
    key: 'name',
    title: '製品名',
    dataIndex: 'name',
    width: productNameWidth,
  },
]

const tableContainerStyles = {
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  bgcolor: '#FFFFFF',
}

const tableStyles = {
  bgcolor: '#FFFFFF',
  minWidth: '100%',
  width: 'max-content',
  // 1列目（code）
  '& .MuiTableCell-head.sticky-left-code': {
    position: 'sticky',
    left: 0,
    // 製品名が隠れないように他のヘッダーより高く、GlobalNavigation の SubMenu より低い z-index
    zIndex: 10,
  },
  '& .MuiTableCell-body.sticky-left-code': {
    position: 'sticky',
    left: 0,
    backgroundColor: '#FFFFFF',
  },
  // 2列目（name）
  '& .MuiTableCell-head.sticky-left-name': {
    position: 'sticky',
    left: productCodeWidth,
    // 製品名が隠れないように他のヘッダーより高く、GlobalNavigation の SubMenu より低い z-index
    zIndex: 10,
  },
  '& .MuiTableCell-body.sticky-left-name': {
    position: 'sticky',
    left: productCodeWidth,
    backgroundColor: '#FFFFFF',
  },
}

const cellBaseStyle = {
  height: '38px',
  backgroundColor: '#FFFFFF',
}

const cellTextBaseStyles = {
  fontFamily: 'Noto Sans JP',
  fontSize: '14px',
  color: semanticColors.colorTextBase,
}

const loadingContainerStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
}

const getRecordValue = (value: ProductMasterRecordValue) => {
  switch (value.fieldType) {
    case MasterFieldTypeEnum.Number:
      return value.numberValue
    case MasterFieldTypeEnum.Text:
      return value.textValue
    default:
      return undefined
  }
}

const getStickyClassName = (index: number): string => {
  if (index === 0) {
    return 'sticky-left-code'
  }
  if (index === 1) {
    return 'sticky-left-name'
  }
  return ''
}

type MasterRecordsTableProps = {
  masterUuid: string
}

const MasterRecordsTable: FC<MasterRecordsTableProps> = ({ masterUuid }) => {
  const { data, isLoading } = useQueryMaster(masterUuid)

  const columns: ColumnDef[] = [
    ...baseColumns,
    ...(data?.fieldDefs ?? []).map(fieldDef => ({
      key: fieldDef.code,
      title: fieldDef.name,
      dataIndex: String(fieldDef.id),
      width: 160,
    })),
  ]

  const dataSource: DataType[] = data
    ? data.records.map(record => ({
        key: record.id,
        name: record.name,
        code: record.code,
        ...Object.fromEntries(
          Object.entries(record.values).map(([key, value]) => [
            key,
            getRecordValue(value),
          ]),
        ),
      }))
    : []

  if (isLoading) {
    return (
      <Box sx={loadingContainerStyles}>
        <CircularProgress />
      </Box>
    )
  }

  const hasNoFieldDefs =
    data !== undefined &&
    data.fieldDefs.length === 0 &&
    data.records.length === 0
  const hasFieldDefsButNoRecords =
    data !== undefined && data.fieldDefs.length > 0 && data.records.length === 0

  return (
    <TableContainer sx={tableContainerStyles}>
      <Table
        sx={{
          ...tableStyles,
          ...(hasNoFieldDefs || hasFieldDefsButNoRecords
            ? { height: '100%' }
            : {}),
        }}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.key}
                size="small"
                className={getStickyClassName(index)}
                sx={{
                  ...cellBaseStyle,
                  borderBottom: '2px solid #DBE1E9',
                  width: column.width,
                }}
              >
                <Typography
                  sx={{
                    ...cellTextBaseStyles,
                    fontWeight: 700,
                  }}
                >
                  {column.title}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {hasNoFieldDefs ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ border: 'none', textAlign: 'center', py: 10 }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '20px',
                    color: semanticColors.colorTextBase,
                    mb: 1,
                  }}
                >
                  まずは項目を設定しましょう！
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    color: semanticColors.colorTextBase,
                  }}
                >
                  右上の「項目を編集する」ボタンから設定できます
                </Typography>
              </TableCell>
            </TableRow>
          ) : hasFieldDefsButNoRecords ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ border: 'none', textAlign: 'center', py: 10 }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '20px',
                    color: semanticColors.colorTextBase,
                    mb: 1,
                  }}
                >
                  次にデータを追加しましょう！
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    color: semanticColors.colorTextBase,
                  }}
                >
                  右上の「データを更新する」ボタンから追加できます
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {dataSource.map((record: DataType) => (
                <TableRow key={record.key} hover>
                  {columns.map((column, index) => (
                    <TableCell
                      key={column.key}
                      size="small"
                      className={getStickyClassName(index)}
                      sx={{
                        ...cellBaseStyle,
                      }}
                    >
                      <Typography
                        sx={{
                          ...cellTextBaseStyles,
                          fontWeight: 400,
                        }}
                      >
                        {String(record[column.dataIndex] ?? '')}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <LimitAlertTableRow
                recordCount={dataSource.length}
                colSpan={columns.length}
              />
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default MasterRecordsTable
